// exams ux/inputs.tsx SegmentedControl — a value picker, so radiogroup semantics (role=radio + aria-checked, roving tabindex, ArrowLeft/ArrowRight/Home/End move selection + focus), kept per-option "${id}-${value}" automation ids; radii mapped to semantic rounded-field/rounded-selector, cursor-pointer added per TW4 button-cursor default change.
import { For, Show, type JSX } from "solid-js";

export interface SegmentOption {
  /** Visible text, one word or two ("Table", "Cards"). */
  label: string;
  /** The stable key the caller stores; also the automation-id suffix. */
  value: string;
  /** A lucide icon ELEMENT before the label, sized by the caller. */
  icon?: JSX.Element;
}

export interface SegmentedControlProps {
  /** The choices in display order. Every one renders, so keep the labels short. */
  options: SegmentOption[];
  /** Controlled: the selected option's `value`. */
  value: string;
  /** Receives the chosen `value` on click or arrow key. */
  onChange: (value: string) => void;
  /** Disables the whole group — for a choice the role cannot change. */
  disabled?: boolean;
  /** Set on the group and used for per-option automation ids (`${id}-${value}`). */
  id?: string;
  /** Names the radiogroup for screen readers ("View") when no visible label does. */
  "aria-label"?: string;
}

/**
 * Pick ONE of two to five values, all visible at once — a view switch
 * (table/cards), a scope (mine/all), a short enum on a form. More options,
 * or options with long labels, want a Select; a mutually exclusive set that
 * changes what the page SHOWS rather than a saved value wants Tabs.
 */
export function SegmentedControl(props: SegmentedControlProps) {
  const buttons: HTMLButtonElement[] = [];
  const selectedIndex = () => props.options.findIndex((o) => o.value === props.value);
  // Roving tabindex: the checked radio is the group's single tab stop (first option when none checked).
  const tabIndexFor = (i: number) => {
    const sel = selectedIndex();
    return (sel === -1 ? i === 0 : sel === i) ? 0 : -1;
  };
  const moveTo = (i: number) => {
    const option = props.options[i];
    if (!option) return;
    props.onChange(option.value);
    buttons[i]?.focus();
  };
  const onKeyDown = (e: KeyboardEvent) => {
    const n = props.options.length;
    if (n === 0) return;
    const current = Math.max(0, selectedIndex());
    if (e.key === "ArrowRight") {
      e.preventDefault();
      moveTo((current + 1) % n);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      moveTo((current - 1 + n) % n);
    } else if (e.key === "Home") {
      e.preventDefault();
      moveTo(0);
    } else if (e.key === "End") {
      e.preventDefault();
      moveTo(n - 1);
    }
  };
  return (
    <div
      id={props.id}
      role="radiogroup"
      aria-label={props["aria-label"]}
      onKeyDown={onKeyDown}
      class="inline-flex items-center gap-0.5 rounded-field border border-base-300 bg-base-100 p-0.5"
    >
      <For each={props.options}>
        {(option, index) => (
          // Selected stays a solid primary FILL (a segmented control marks selection by
          // fill, not tint); unselected hover/down/key-focus ride the --ui-state-* ramp (tokens.css).
          <button
            type="button"
            role="radio"
            ref={(el) => (buttons[index()] = el)}
            id={props.id ? `${props.id}-${option.value}` : undefined}
            aria-checked={props.value === option.value}
            tabindex={tabIndexFor(index())}
            disabled={props.disabled}
            onClick={() => props.onChange(option.value)}
            class={`inline-flex cursor-pointer items-center gap-1.5 rounded-selector px-3 py-1.5 text-sm font-medium transition-colors ${
              props.value === option.value
                ? "bg-primary text-primary-content"
                : "text-base-muted hover:bg-(--ui-state-hover) focus-visible:bg-(--ui-state-hover) active:bg-(--ui-state-down)"
            }`}
          >
            <Show when={option.icon}>{option.icon}</Show>
            {option.label}
          </button>
        )}
      </For>
    </div>
  );
}
